import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Package, KeyRound, Target, Github, CheckCircle2, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";

/**
 * Onboarding Flow for SaaS Mode
 *
 * Steps:
 * 1. Welcome - Explain what Paxley does
 * 2. Connect GitHub - OAuth with GitHub App
 * 3. Select Repos - Choose which repos to scan
 * 4. Complete - Show first scan status
 */

const GITHUB_APP_NAME = process.env.REACT_APP_GITHUB_APP_NAME || "paxley-security";
const API_URL = process.env.REACT_APP_API_URL || "";

type OnboardingStep = "welcome" | "connecting" | "repos" | "complete";

interface Repo {
  id: number;
  full_name: string;
  private: boolean;
  selected: boolean;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<OnboardingStep>("welcome");
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check for GitHub callback
  useEffect(() => {
    const installationId = searchParams.get("installation_id");
    const setupAction = searchParams.get("setup_action");

    if (installationId && setupAction === "install") {
      // User just installed the GitHub App
      setStep("repos");
      fetchInstalledRepos(installationId);
    }
  }, [searchParams]);

  async function fetchInstalledRepos(installationId: string) {
    setLoading(true);
    setError(null);

    try {
      const { apiFetch } = await import("../api/client");
      const data = await apiFetch<{ repositories: Array<{ id: number; full_name: string; private: boolean }> }>({
        path: `/api/installations/${installationId}/repos`,
      });

      setRepos(
        data.repositories.map((r) => ({
          id: r.id,
          full_name: r.full_name,
          private: r.private,
          selected: true, // Default to selected
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  function handleConnectGitHub() {
    // Redirect to GitHub App installation
    const installUrl = `https://github.com/apps/${GITHUB_APP_NAME}/installations/new`;
    window.location.href = installUrl;
  }

  function toggleRepo(id: number) {
    setRepos((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r))
    );
  }

  async function handleComplete() {
    setStep("complete");

    // Persist the user's repo selection so deselected repos are excluded from scanning
    try {
      const selectedIds = repos.filter((r) => r.selected).map((r) => r.id);
      const { apiFetch } = await import("../api/client");
      await apiFetch({ method: "PUT", path: "/v1/repos/selection", body: { repo_ids: selectedIds } });
    } catch (e) {
      // Non-blocking -- repos will still be available; worst case all are scanned
      console.warn("Failed to save repo selection:", e);
    }

    // Wait a moment then redirect
    redirectTimerRef.current = setTimeout(() => {
      navigate("/dashboard");
    }, 3000);
  }

  // Cleanup redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const steps = ["welcome", "connecting", "repos", "complete"];
  const currentIndex = steps.indexOf(step);

  return (
    <div className="min-h-screen bg-bg">

      <div className="relative mx-auto max-w-2xl px-6 py-16">
        {/* Progress indicator */}
        <div className="mb-12 flex items-center justify-center gap-2">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <Badge
                variant={step === s ? "default" : i < currentIndex ? "secondary" : "outline"}
                className={`h-3 w-3 rounded-full p-0 ${
                  step === s ? "ring-4 ring-brand/20 animate-pulse" : ""
                }`}
              />
              {i < 3 && <div className="h-0.5 w-8 bg-surface3" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <Card className="border-border/30 bg-surface1">
          <CardContent className="p-8">
            {step === "welcome" && (
              <WelcomeStep onContinue={() => setStep("connecting")} />
            )}

            {step === "connecting" && (
              <ConnectingStep onConnect={handleConnectGitHub} />
            )}

            {step === "repos" && (
              <ReposStep
                repos={repos}
                loading={loading}
                error={error}
                onToggle={toggleRepo}
                onComplete={handleComplete}
              />
            )}

            {step === "complete" && <CompleteStep />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------
// Step Components
// ---------------------------------

function WelcomeStep({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="text-center">
      <img src="/logo.png" alt="Paxley" className="mb-6 h-20 w-20 rounded-xl mx-auto" />
      <h1 className="mb-4 text-3xl font-bold text-text">Welcome to Paxley</h1>
      <p className="mb-8 text-lg text-text2">
        Secure your code with intelligent security scanning.
        <br />
        Zero noise for developers. Full visibility for security teams.
      </p>

      <div className="mb-8 grid gap-4 text-left">
        <Feature icon={<Search className="h-6 w-6 text-brand" />} title="SAST Scanning" desc="Find vulnerabilities in your code" />
        <Feature icon={<Package className="h-6 w-6 text-info" />} title="Dependency Scanning" desc="Detect vulnerable packages" />
        <Feature icon={<KeyRound className="h-6 w-6 text-medium" />} title="Secrets Detection" desc="Catch leaked credentials" />
        <Feature icon={<Target className="h-6 w-6 text-success" />} title="Smart Filtering" desc="Only show new issues in PRs" />
      </div>

      <Button onClick={onContinue} size="lg" className="px-8 py-3 text-lg">
        Get Started
      </Button>
    </div>
  );
}

function Feature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Card className="border-border/30 bg-surface2/50">
      <CardContent className="flex items-start gap-4 p-4">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div>
          <div className="font-semibold text-text">{title}</div>
          <div className="text-sm text-muted">{desc}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectingStep({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="text-center">
      <div className="mb-6">
        <Github className="mx-auto h-16 w-16 text-white" />
      </div>
      <h2 className="mb-4 text-2xl font-bold text-text">Connect Your GitHub</h2>
      <p className="mb-8 text-text2">
        Install the Paxley GitHub App to start scanning your repositories.
        <br />
        <span className="text-sm text-muted">
          We only need read access to your code. Scans are processed securely.
        </span>
      </p>

      <Button variant="secondary" onClick={onConnect} size="lg" className="px-8 py-3 text-lg gap-2">
        <Github className="h-6 w-6" />
        Install GitHub App
      </Button>

      <div className="mt-6 space-y-1 text-sm text-muted">
        <p className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> Read-only access to code</p>
        <p className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> Write access to PR comments only</p>
        <p className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> No access to your other GitHub data</p>
      </div>
    </div>
  );
}

function ReposStep({
  repos,
  loading,
  error,
  onToggle,
  onComplete,
}: {
  repos: Repo[];
  loading: boolean;
  error: string | null;
  onToggle: (id: number) => void;
  onComplete: () => void;
}) {
  const selectedCount = repos.filter((r) => r.selected).length;

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold text-text">Select Repositories</h2>
      <p className="mb-6 text-text2">
        Choose which repositories to scan. You can change this later.
      </p>

      {loading && (
        <div className="py-12 text-center text-muted">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-brand" />
          Loading repositories...
        </div>
      )}

      {error && (
        <Card className="border-critical/20 bg-critical/10">
          <CardContent className="p-4 text-critical">
            {error}
          </CardContent>
        </Card>
      )}

      {!loading && !error && (
        <>
          <div className="mb-6 max-h-80 overflow-y-auto rounded-lg border border-border stagger-fade-in">
            {repos.map((repo) => (
              <label
                key={repo.id}
                className="flex cursor-pointer items-center gap-3 border-b border-divider px-4 py-3 last:border-0 hover:bg-surface2/50"
              >
                <input
                  type="checkbox"
                  checked={repo.selected}
                  onChange={() => onToggle(repo.id)}
                  className="h-5 w-5 rounded border-border bg-surface1 text-brand focus:ring-brand"
                />
                <span className="flex-1 font-mono text-sm text-text">
                  {repo.full_name}
                </span>
                {repo.private && (
                  <Badge variant="secondary">Private</Badge>
                )}
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">
              {selectedCount} of {repos.length} selected
            </span>
            <Button
              onClick={onComplete}
              disabled={selectedCount === 0}
            >
              Start Scanning
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function CompleteStep() {
  return (
    <div className="py-8 text-center">
      <div className="mb-6">
        <CheckCircle2 className="mx-auto h-16 w-16 text-success" />
      </div>
      <h2 className="mb-4 text-2xl font-bold text-text">You're All Set!</h2>
      <p className="mb-6 text-text2">
        Your initial baseline scans are starting now.
        <br />
        Results will appear on your dashboard in a few minutes.
      </p>

      <Card className="mb-8 border-brand/20 bg-brand/5">
        <CardContent className="p-4">
          <div className="text-sm text-brand">
            <p className="font-semibold">What happens next?</p>
            <ul className="mt-2 space-y-1 text-left">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Paxley scans your repositories</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Baseline of existing issues is established</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Future PRs will only show NEW issues</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Security team can view all findings on the dashboard</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-muted">Redirecting to dashboard...</p>
    </div>
  );
}
